import { inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

import { BaseVehicle, CustomerVehicleRelationType, EditVehicleRequest } from '../../../../model/customer-vehicle.model';
import { UpdateC2vRequest } from '../../model/requests/update-c2v.model';
import { RetailCrmVehicle } from '../../model/responses/vehicles.model';
import { RetailCrmFoundCustomerViewModelMapper } from '../view-model/found-customer-model.mapper';

@Injectable({
  providedIn: 'root',
})
export class CustomerVehicleMapper {
  private readonly logger = inject(NGXLogger);
  private readonly foundCustomerMapper = inject(RetailCrmFoundCustomerViewModelMapper);

  private readonly relationTypesMapping: [CustomerVehicleRelationType, 'owner' | 'keeper' | 'user'][] = [
    [CustomerVehicleRelationType.OWNER, 'owner'],
    [CustomerVehicleRelationType.KEEPER, 'keeper'],
    [CustomerVehicleRelationType.USER, 'user'],
  ];

  /**
   * Creates a UpdateC2vRequest used for updating C2V relationships in Retail CRM.
   */
  mapToUpdateC2vRequest(changeRequest: EditVehicleRequest): UpdateC2vRequest {
    if (!changeRequest.vin || !changeRequest.accountNumber) {
      const msg = `#CDFTF0245 VIN(${changeRequest.vin}) and accountNumber(${changeRequest.accountNumber}) is required to map to UpdateC2vRequest`;
      this.logger.error(msg);
      throw new Error(msg);
    }

    // Check all passed relations are unique over add/remove sets
    const allRelations = [...changeRequest.addRelationTypes, ...changeRequest.removeRelationTypes];
    const uniqueRelations = new Set(allRelations);
    if (uniqueRelations.size !== allRelations.length) {
      const msg = `#CDFTF0243 Relation types across add/remove sets must be unique. Received add: {${[
        ...changeRequest.addRelationTypes,
      ].join(', ')}}, remove: {${[...changeRequest.removeRelationTypes].join(', ')}}`;
      this.logger.error(msg);
      throw new Error(msg);
    }

    // Warn if unknown relation types are included
    const knownRelationTypes = this.relationTypesMapping.map(([relationType]) => relationType);
    allRelations.forEach((relationType) => {
      if (!knownRelationTypes.includes(relationType)) {
        this.logger.warn(
          `#CDFTF0244 Unknown relation type ${relationType} included in vehicle with VIN ${changeRequest.vin}; Relation type will be ignored`,
        );
      }
    });

    const updateC2vReq: UpdateC2vRequest = {
      vin: changeRequest.vin,
    };

    for (const [relationType, relationKey] of this.relationTypesMapping) {
      // Check if we want to add this type
      if (changeRequest.addRelationTypes.has(relationType)) {
        updateC2vReq[relationKey] = changeRequest.accountNumber;
      }

      // Check if we want to remove this type
      if (changeRequest.removeRelationTypes.has(relationType)) {
        updateC2vReq[relationKey] = ''; // Empty means remove; Key not set, undefined or null means no change
      }

      // ELSE: We don't want to change this relation type
    }

    if (changeRequest.registrationNumber !== undefined) {
      updateC2vReq.registrationNumber = changeRequest.registrationNumber;
    }

    return updateC2vReq;
  }

  mapToCustomerVehicle(vehicleResponse: RetailCrmVehicle): BaseVehicle {
    return {
      name: vehicleResponse.name,
      allCustomers: {
        [CustomerVehicleRelationType.OWNER]: this.foundCustomerMapper.mapVehicleRelationToFoundCustomer(
          vehicleResponse.owner,
          CustomerVehicleRelationType.OWNER,
        ),
        [CustomerVehicleRelationType.KEEPER]: this.foundCustomerMapper.mapVehicleRelationToFoundCustomer(
          vehicleResponse.keeper,
          CustomerVehicleRelationType.KEEPER,
        ),
        [CustomerVehicleRelationType.USER]: this.foundCustomerMapper.mapVehicleRelationToFoundCustomer(
          vehicleResponse.user,
          CustomerVehicleRelationType.USER,
        ),
      },
      licensePlate: vehicleResponse.registrationNumber,
      vin: vehicleResponse.vin,
    };
  }
}
